import { useMemo } from 'react'
import { useUserContext } from '@/UserContext'
import { hasPinnedPresentation, useViewMode } from '../../../shared/useViewMode'
import { VideoSource } from '../../../shared/Video2'
import * as Constants from '@/services/Constants'
import { useVideoGridContainerRef } from './useVideoGridContainerRef'
import { VideoGridVideo } from './VideoGridVideo'
import { PinnedPresentationOverlay } from '../../../shared/PinnedPresentationOverlay'
import { VideoGridPagination } from './VideoGridPagination'
import { LocalTrack } from 'livekit-client'

export function TestVideoGrid({ numVideos, page }: { numVideos: number; page?: number }) {
    const [userState] = useUserContext()
    const viewMode = useViewMode()

    const vids = useMemo(() => {
        let vids: VideoSource[] = []
        for (let i = 0; i < numVideos; i++) {
            if (userState.mediaStreams.video instanceof LocalTrack) {
                const vid = new VideoSource(
                    'me',
                    'camera',
                    userState.mediaStreams.video,
                    userState.mediaStreams.videoProcessor?.id
                )
                vids.push(vid)
            }
        }
        if (vids.length > Constants.max_videos_per_page) {
            const pg = page ?? 0
            vids = vids.slice(pg * Constants.max_videos_per_page, (pg + 1) * Constants.max_videos_per_page)
        }

        return vids
    }, [userState.mediaStreams.video, numVideos, page])

    const container = useVideoGridContainerRef(vids.length, numVideos, viewMode.sidebarCollapsed, viewMode.fullscreen)

    return (
        <div
            id="video-grid"
            className="relative w-full h-full min-h-0 overflow-y-auto flex flex-wrap justify-center items-center box-border"
            ref={container}
        >
            {vids.map((vid, i) => (
                <VideoGridVideo
                    key={vid.id}
                    vid={vid}
                    userState={userState}
                    viewMode={viewMode}
                    numVideos={numVideos}
                    index={i}
                />
            ))}

            {hasPinnedPresentation(viewMode) && <PinnedPresentationOverlay fullscreen={viewMode.fullscreen} />}
            <VideoGridPagination viewMode={viewMode} numVideos={numVideos} />
        </div>
    )
}
